import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import {
  Hash,
  Plus,
  Send,
  Loader2,
  Megaphone,
  Calendar,
  ChevronDown,
  Users,
  X,
} from "lucide-react";

import {
  fetchClubChannels,
  createClubChannel,
  fetchChannelMessages,
  sendClubChannelMessage,
} from "../../services/clubsApi";
import ClubMembersTab from "../../pages/Clubs/ClubMembersTab";
import { useAuth } from "../../context/AuthContext";

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

function resolveAvatar(url) {
  if (!url) return null;
  let s = String(url).trim();
  if (!s) return null;
  if (s.startsWith("undefined/")) s = s.replace(/^undefined\/+/, "");
  if (/^https?:\/\//i.test(s)) return s;
  const apiBase = (process.env.REACT_APP_API_BASE || "").trim();
  const rooted = s.startsWith("/") ? s : `/${s}`;
  if (apiBase) return `${apiBase.replace(/\/+$/, "")}${rooted}`;
  return rooted;
}

function formatTs(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function isModMembership(m) {
  const r = String(m?.role || "").toLowerCase();
  return ["owner", "moderator", "mod", "admin"].includes(r);
}

function pickViewerId(user) {
  const keys = [
    user?.id,
    user?.user_id,
    user?.userId,
    user?.uid,
    user?.sub,
    user?.profile?.id,
    user?.profile?.user_id,
    user?.account?.id,
    user?.account?.user_id,
  ];
  for (const v of keys) {
    if (v == null || v === "") continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function ChannelTypeIcon({ type }) {
  const t = String(type || "text").toLowerCase();
  if (t === "announcement") return <Megaphone className="h-3.5 w-3.5 text-amber-200/90 shrink-0" />;
  if (t === "event") return <Calendar className="h-3.5 w-3.5 text-sky-200/90 shrink-0" />;
  return <Hash className="h-3.5 w-3.5 text-white/45 shrink-0" />;
}

export default function ClubChannelsEnvironment({
  slug,
  club,
  membership,
  accent = "emerald",
  /** When set with onMembersOpenChange, shell (ClubHome) controls members panel — no duplicate rail. */
  membersOpen: shellMembersOpen,
  onMembersOpenChange,
}) {
  const { user } = useAuth();
  const viewerId = pickViewerId(user);

  const listRef = useRef(null);
  const olderOffsetRef = useRef(0);
  const msgLoadSeqRef = useRef(0); // Prevent out-of-order message fetches while switching channels
  const pollSeqRef = useRef(0); // Prevent out-of-order polling fetches while switching channels
  const activeIdRef = useRef(null);
  const busyRef = useRef(false);

  const socketRef = useRef(null);
  const [wsJoinedChannelId, setWsJoinedChannelId] = useState(null);
  const wsConnectedRef = useRef(false);

  const [typingUsersById, setTypingUsersById] = useState({});
  const typingStopTimeoutRef = useRef(null);
  const typingActiveRef = useRef(false);
  const prevActiveIdRef = useRef(null);
  const typingStopDelayMs = 2000;

  const canUseChannels = useMemo(() => {
    const s = String(membership?.status || "").toLowerCase();
    return s === "active";
  }, [membership]);

  const canModerate = useMemo(() => isModMembership(membership), [membership]);

  const [channels, setChannels] = useState([]);
  const [channelsErr, setChannelsErr] = useState("");
  const [channelsLoading, setChannelsLoading] = useState(true);

  const [activeId, setActiveId] = useState(null);

  const [messages, setMessages] = useState([]);
  const [msgErr, setMsgErr] = useState("");
  const [msgLoading, setMsgLoading] = useState(false);
  const [hasOlder, setHasOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);

  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("text");
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState("");

  const [localMobileMembersOpen, setLocalMobileMembersOpen] = useState(false);

  // Keep refs in sync immediately (helps ensure in-flight polling never applies to a
  // previously active channel).
  activeIdRef.current = activeId;
  busyRef.current = Boolean(msgLoading || loadingOlder || sending);

  const membersShellControlled =
    typeof onMembersOpenChange === "function" && typeof shellMembersOpen === "boolean";

  const membersPanelOpen = membersShellControlled ? shellMembersOpen : localMobileMembersOpen;

  const setMembersPanelOpen = useCallback(
    (next) => {
      if (membersShellControlled) {
        onMembersOpenChange(typeof next === "function" ? next(shellMembersOpen) : next);
      } else {
        setLocalMobileMembersOpen(typeof next === "function" ? next(localMobileMembersOpen) : next);
      }
    },
    [membersShellControlled, onMembersOpenChange, shellMembersOpen, localMobileMembersOpen]
  );

  const showMembersDesktopColumn = membersShellControlled ? membersPanelOpen : true;

  const activeChannel = useMemo(
    () => channels.find((c) => c.id === activeId) || null,
    [channels, activeId]
  );

  const canPostInActiveChannel = useMemo(() => {
    if (!activeChannel) return false;
    const t = String(activeChannel.channel_type || "text").toLowerCase();
    if (t === "announcement" && !canModerate) return false;
    return true;
  }, [activeChannel, canModerate]);

  const typingUsersForUI = useMemo(() => {
    const vid = viewerId != null ? Number(viewerId) : null;
    const entries = Object.entries(typingUsersById);
    return entries
      .filter(([uid]) => {
        const n = Number(uid);
        return vid == null || n !== vid;
      })
      .map(([uid, info]) => ({ userId: Number(uid), username: info?.username || `User ${uid}` }));
  }, [typingUsersById, viewerId]);

  const typingIndicatorText = useMemo(() => {
    const n = typingUsersForUI.length;
    if (n === 0) return "";
    if (n === 1) return `${typingUsersForUI[0].username} is typing...`;
    return `${n} people are typing...`;
  }, [typingUsersForUI]);

  const announcementChannels = useMemo(
    () =>
      channels.filter(
        (ch) => String(ch?.channel_type || "").toLowerCase() === "announcement"
      ),
    [channels]
  );

  const textChannels = useMemo(
    () =>
      channels.filter(
        (ch) => String(ch?.channel_type || "").toLowerCase() !== "announcement"
      ),
    [channels]
  );

  const accentRing =
    accent === "sky"
      ? "focus-visible:ring-sky-400/30 border-sky-300/20"
      : "focus-visible:ring-emerald-400/30 border-emerald-300/20";

  const accentBtn =
    accent === "sky"
      ? "bg-sky-400/12 border-sky-300/25 text-sky-100 hover:bg-sky-400/18"
      : "bg-emerald-400/12 border-emerald-300/25 text-emerald-100 hover:bg-emerald-400/18";

  const loadChannels = useCallback(async () => {
    if (!slug || !canUseChannels) {
      setChannels([]);
      setChannelsLoading(false);
      return;
    }
    setChannelsLoading(true);
    setChannelsErr("");
    try {
      const data = await fetchClubChannels(slug);
      const raw = Array.isArray(data?.channels) ? data.channels : [];
      const list = raw
        .map((ch) => {
          const id = Number(ch.id);
          return Number.isFinite(id) ? { ...ch, id } : null;
        })
        .filter(Boolean);
      setChannels(list);
      setActiveId((prev) => {
        const prevNum = Number(prev);
        if (Number.isFinite(prevNum) && list.some((c) => c.id === prevNum)) return prevNum;
        return list[0]?.id ?? null;
      });
    } catch (e) {
      setChannelsErr(e?.message || "Could not load channels");
      setChannels([]);
    } finally {
      setChannelsLoading(false);
    }
  }, [slug, canUseChannels]);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  // --- WebSocket real-time updates (V1) ---
  // If socket join succeeds, we pause polling to avoid conflicting updates.
  useEffect(() => {
    if (!slug || !canUseChannels) return;

    const serverUrl =
      process.env.NODE_ENV === "development" ? "http://localhost:5000" : window.location.origin;

    const socket = io(serverUrl, {
      path: "/socket.io/",
      transports: ["polling", "websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      withCredentials: true,
    });

    socketRef.current = socket;
    wsConnectedRef.current = false;
    setWsJoinedChannelId(null);

    socket.on("connect", () => {
      wsConnectedRef.current = true;
    });

    socket.on("disconnect", () => {
      wsConnectedRef.current = false;
      setWsJoinedChannelId(null);
      setTypingUsersById({});
      typingActiveRef.current = false;
      if (typingStopTimeoutRef.current) {
        clearTimeout(typingStopTimeoutRef.current);
        typingStopTimeoutRef.current = null;
      }
    });

    socket.on("connect_error", () => {
      wsConnectedRef.current = false;
      setWsJoinedChannelId(null);
      setTypingUsersById({});
      typingActiveRef.current = false;
      if (typingStopTimeoutRef.current) {
        clearTimeout(typingStopTimeoutRef.current);
        typingStopTimeoutRef.current = null;
      }
    });

    socket.on("club-channel:joined", (data) => {
      const n = Number(data?.channelId);
      if (Number.isFinite(n) && n === Number(activeIdRef.current)) {
        setWsJoinedChannelId(n);
      }
    });

    socket.on("club-channel:message", (payload) => {
      const message = payload?.message;
      if (!message) return;

      const msgChannelId = Number(message.channel_id);
      const active = Number(activeIdRef.current);
      if (!Number.isFinite(msgChannelId) || msgChannelId !== active) return;

      const incomingId = Number(message.id);
      if (!Number.isFinite(incomingId)) return;

      setMessages((prev) => {
        const prevIds = new Set(prev.map((m) => m?.id));
        if (prevIds.has(incomingId)) return prev;

        const el = listRef.current;
        const atBottom =
          el ? el.scrollHeight - (el.scrollTop + el.clientHeight) <= 80 : true;

        olderOffsetRef.current += 1;

        requestAnimationFrame(() => {
          const el2 = listRef.current;
          if (!el2) return;
          if (atBottom) el2.scrollTop = el2.scrollHeight;
        });

        return [...prev, message];
      });
    });

    socket.on("club-channel:typing:start", (payload) => {
      const channelId = Number(payload?.channelId);
      if (!Number.isFinite(channelId) || channelId !== Number(activeIdRef.current)) return;
      const userId = Number(payload?.userId);
      if (!Number.isFinite(userId)) return;

      const username =
        typeof payload?.username === "string" && payload.username.trim()
          ? payload.username.trim()
          : `User ${userId}`;

      setTypingUsersById((prev) => {
        if (prev[userId]?.username === username) return prev;
        return { ...prev, [userId]: { username } };
      });
    });

    socket.on("club-channel:typing:stop", (payload) => {
      const channelId = Number(payload?.channelId);
      if (!Number.isFinite(channelId) || channelId !== Number(activeIdRef.current)) return;
      const userId = Number(payload?.userId);
      if (!Number.isFinite(userId)) return;

      setTypingUsersById((prev) => {
        if (!prev[userId]) return prev;
        const next = { ...prev };
        delete next[userId];
        return next;
      });
    });


    socket.on("club-channel:error", (data) => {
      const err = data?.error || data?.message;
      if (typeof err === "string" && err.trim()) {
        setMsgErr(err.trim());
      }
    });

    return () => {
      try {
        socket.disconnect();
      } catch {
        // ignore
      }
      socketRef.current = null;
      wsConnectedRef.current = false;
      setWsJoinedChannelId(null);
      setTypingUsersById({});
    };
  }, [slug, canUseChannels]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !activeId || !canUseChannels) {
      setWsJoinedChannelId(null);
      return;
    }

    setWsJoinedChannelId(null); // Wait for server ack.
    socket.emit("club-channel:join", { slug, channelId: activeId });
  }, [activeId, slug, canUseChannels]);

  const loadMessages = useCallback(async (channelId, { reset } = { reset: true }) => {
    if (!slug || !channelId) return;
    const seq = ++msgLoadSeqRef.current;
    if (reset) {
      olderOffsetRef.current = 0;
      setMsgLoading(true);
      setMessages([]);
    }
    setMsgErr("");
    try {
      const off = reset ? 0 : olderOffsetRef.current;
      const data = await fetchChannelMessages(slug, channelId, {
        limit: 50,
        offset: off,
      });
      if (seq !== msgLoadSeqRef.current) return; // Ignore stale response
      const batch = Array.isArray(data?.messages) ? data.messages : [];
      if (reset) {
        setMessages(batch);
        olderOffsetRef.current = batch.length;
        requestAnimationFrame(() => {
          if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
        });
      } else {
        const el = listRef.current;
        const prevTop = el?.scrollTop ?? 0;
        const prevHeight = el?.scrollHeight ?? 0;
        setMessages((prev) => [...batch, ...prev]);
        olderOffsetRef.current += batch.length;
        requestAnimationFrame(() => {
          const el2 = listRef.current;
          if (!el2) return;
          el2.scrollTop = el2.scrollHeight - prevHeight + prevTop;
        });
      }
      setHasOlder(Boolean(data?.hasMore));
    } catch (e) {
      if (seq !== msgLoadSeqRef.current) return; // Ignore stale error
      setMsgErr(e?.message || "Could not load messages");
      if (reset) setMessages([]);
    } finally {
      if (seq === msgLoadSeqRef.current) {
        setMsgLoading(false);
        setLoadingOlder(false);
      }
    }
  }, [slug]);

  useEffect(() => {
    if (!activeId || !canUseChannels) {
      setMessages([]);
      return;
    }
    loadMessages(activeId, { reset: true });
  }, [activeId, canUseChannels, loadMessages]);

  // Clear typing state when switching channels (and stop our own typing immediately).
  useEffect(() => {
    const prevId = prevActiveIdRef.current;
    if (typingActiveRef.current && prevId != null && socketRef.current) {
      try {
        socketRef.current.emit("club-channel:typing:stop", { channelId: prevId });
      } catch {
        // ignore
      }
    }

    typingActiveRef.current = false;
    prevActiveIdRef.current = activeId;

    if (typingStopTimeoutRef.current) {
      clearTimeout(typingStopTimeoutRef.current);
      typingStopTimeoutRef.current = null;
    }

    setTypingUsersById({});
  }, [activeId, canUseChannels]);

  useEffect(() => {
    return () => {
      if (typingStopTimeoutRef.current) clearTimeout(typingStopTimeoutRef.current);
      typingStopTimeoutRef.current = null;
      typingActiveRef.current = false;

      // Best-effort: stop typing on unmount.
      if (socketRef.current && prevActiveIdRef.current != null) {
        try {
          socketRef.current.emit("club-channel:typing:stop", { channelId: prevActiveIdRef.current });
        } catch {
          // ignore
        }
      }
    };
  }, []);

  // Lightweight polling for new messages in the currently active channel.
  // Poll interval is intentionally small; polling fetches the newest page (offset=0)
  // and merges by message id to avoid re-sorting or disrupting scroll.
  const pollIntervalMs = 4000;
  useEffect(() => {
    if (!slug || !activeId || !canUseChannels) return;
    // If the socket successfully joined this channel room, we pause polling.
    if (wsJoinedChannelId === activeId) return;

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (busyRef.current) return;

      const seq = ++pollSeqRef.current;
      const channelIdAtRequest = activeId;

      try {
        const data = await fetchChannelMessages(slug, channelIdAtRequest, {
          limit: 50,
          offset: 0,
        });

        // Stale response protection: ignore if channel changed or a newer poll started.
        if (cancelled) return;
        if (seq !== pollSeqRef.current) return;
        if (activeIdRef.current !== channelIdAtRequest) return;

        const batch = Array.isArray(data?.messages) ? data.messages : [];
        if (!batch.length) return;

        setMessages((prev) => {
          const prevIds = new Set(prev.map((m) => m?.id));
          const newOnes = batch.filter((m) => m?.id != null && !prevIds.has(m.id));
          if (!newOnes.length) return prev;

          const el = listRef.current;
          const atBottom =
            el ? el.scrollHeight - (el.scrollTop + el.clientHeight) <= 80 : true;

          // Keep older pagination offset contiguous with the new newest messages appended.
          olderOffsetRef.current += newOnes.length;

          requestAnimationFrame(() => {
            const el2 = listRef.current;
            if (!el2) return;
            if (atBottom) el2.scrollTop = el2.scrollHeight;
          });

          return [...prev, ...newOnes];
        });
      } catch {
        // Best-effort polling: ignore to avoid noisy UI flicker.
      }
    };

    // Poll immediately, then on interval.
    const immediate = setTimeout(poll, 500);
    const intervalId = setInterval(poll, pollIntervalMs);

    return () => {
      cancelled = true;
      clearTimeout(immediate);
      clearInterval(intervalId);
    };
  }, [slug, activeId, canUseChannels, wsJoinedChannelId]);

  const onSend = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || !activeId || sending || !canUseChannels || !canPostInActiveChannel) return;
    try {
      setSending(true);
      setMsgErr("");
      const res = await sendClubChannelMessage(slug, activeId, text);
      const m = res?.message;
      if (m) {
        setMessages((prev) => {
          const prevIds = new Set(prev.map((x) => x?.id));
          if (prevIds.has(m.id)) return prev;
          olderOffsetRef.current += 1;

          return [...prev, m];
        });
        requestAnimationFrame(() => {
          if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
        });
      }
      // Stop typing indicator immediately after sending.
      if (typingStopTimeoutRef.current) {
        clearTimeout(typingStopTimeoutRef.current);
        typingStopTimeoutRef.current = null;
      }
      typingActiveRef.current = false;
      if (socketRef.current && activeId) {
        try {
          socketRef.current.emit("club-channel:typing:stop", { channelId: activeId });
        } catch {
          // ignore
        }
      }
      setDraft("");
    } catch (e2) {
      setMsgErr(e2?.message || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const onLoadOlder = async () => {
    if (!activeId || loadingOlder || !hasOlder || msgLoading) return;
    setLoadingOlder(true);
    await loadMessages(activeId, { reset: false });
  };

  const onCreateChannel = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name || creating) return;
    try {
      setCreating(true);
      setCreateErr("");
      const res = await createClubChannel(slug, {
        name,
        channel_type: newType,
      });
      const ch = res?.channel;
      if (ch) {
        const nid = Number(ch.id);
        const row = Number.isFinite(nid) ? { ...ch, id: nid } : ch;
        setChannels((prev) => [...prev, row]);
        setActiveId(Number.isFinite(nid) ? nid : ch.id);
        setCreateOpen(false);
        setNewName("");
        setNewType("text");
      }
    } catch (e3) {
      setCreateErr(e3?.message || "Could not create channel");
    } finally {
      setCreating(false);
    }
  };

  if (!canUseChannels) {
    return (
      <div className="rounded-2xl border border-white/12 bg-white/[0.02] p-8 text-center max-w-lg mx-auto">
        <div className="text-white font-semibold">Channels are for members</div>
        <p className="mt-2 text-sm text-white/55 leading-relaxed">
          Join this club to browse text channels, separate from the public feed. Channels are
          structured spaces for ongoing chat; the feed is for posts and slower discussion.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col xl:flex-row gap-4 min-h-[520px]">
      {/* Sidebar */}
      <aside
        className={cn(
          "w-full xl:w-[260px] shrink-0 rounded-2xl border border-white/10 bg-[#0a0f1a] overflow-hidden flex flex-col max-h-[40vh] xl:max-h-none xl:h-[min(70vh,720px)]"
        )}
      >
        <div className="px-3 py-3 border-b border-white/10 flex items-center justify-between gap-2 bg-black/25">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/45">
            {club?.name ? String(club.name).slice(0, 28) : "Club"}
          </div>
          {canModerate ? (
            <button
              type="button"
              onClick={() => {
                setCreateOpen(true);
                setCreateErr("");
              }}
              className={cn(
                "h-8 w-8 rounded-lg border border-white/12 inline-flex items-center justify-center text-white/70 hover:text-white hover:bg-white/[0.06] transition",
                accent === "sky" ? "hover:border-sky-300/25" : "hover:border-emerald-300/25"
              )}
              title="Create channel"
            >
              <Plus className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        <div className="flex-1 overflow-y-auto py-2 px-2 min-h-0">
          {channelsLoading ? (
            <div className="flex items-center gap-2 text-white/45 text-sm px-2 py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading channels…
            </div>
          ) : channelsErr ? (
            <div className="rounded-lg border border-red-400/20 bg-red-500/10 px-2.5 py-2 text-sm text-red-200/90">
              {channelsErr}
            </div>
          ) : channels.length === 0 ? (
            <div className="text-sm text-white/50 px-2 py-3 leading-relaxed">
              No channels yet. Refresh or reopen—default channels (`general` + `announcements`) are provisioned automatically.
            </div>
          ) : (
            <div>
              <div className="px-2 pt-1 pb-2 text-[10px] font-bold uppercase tracking-[0.14em] text-white/35">
                Channels
              </div>
              {textChannels.length ? (
                <div className="mt-1">
                  <div className="px-2 pb-2 text-[10px] font-bold uppercase tracking-[0.14em] text-white/35">
                    Text
                  </div>
                  <ul className="space-y-0.5">
                    {textChannels.map((ch) => {
                      const active = ch.id === activeId;
                      return (
                        <li key={ch.id}>
                          <button
                            type="button"
                            onClick={() => setActiveId(ch.id)}
                            className={cn(
                              "w-full text-left rounded-lg px-2 py-1.5 flex items-center gap-2 text-[15px] transition",
                              active
                                ? accent === "sky"
                                  ? "bg-sky-500/22 border border-sky-300/25 text-white font-semibold"
                                  : "bg-emerald-500/20 border border-emerald-300/25 text-white font-semibold"
                                : "text-white/65 hover:bg-white/[0.06] hover:text-white/90 border border-transparent"
                            )}
                          >
                            <ChannelTypeIcon type={ch.channel_type} />
                            <span className="truncate">{ch.name}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}

              {announcementChannels.length ? (
                <div className="mt-3">
                  <div className="px-2 pb-2 text-[10px] font-bold uppercase tracking-[0.14em] text-amber-200/55">
                    Announcements
                  </div>
                  <ul className="space-y-0.5">
                    {announcementChannels.map((ch) => {
                      const active = ch.id === activeId;
                      return (
                        <li key={ch.id}>
                          <button
                            type="button"
                            onClick={() => setActiveId(ch.id)}
                            className={cn(
                              "w-full text-left rounded-lg px-2 py-1.5 flex items-center gap-2 text-[15px] transition",
                              active
                                ? "bg-amber-500/22 border border-amber-300/25 text-white font-semibold"
                                : "text-white/65 hover:bg-white/[0.06] hover:text-white/90 bg-amber-500/6 border border-amber-300/15"
                            )}
                          >
                            <ChannelTypeIcon type={ch.channel_type} />
                            <span className="truncate">{ch.name}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </aside>

      {/* Main chat column */}
      <div className="flex-1 min-w-0 min-h-0 flex flex-col rounded-2xl border border-white/10 bg-[#0c111c] overflow-hidden h-[min(70vh,720px)]">
        <header className="shrink-0 px-4 py-3 border-b border-white/10 flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl border border-white/12 bg-white/[0.04] flex items-center justify-center">
            {activeChannel ? (
              <ChannelTypeIcon type={activeChannel.channel_type} />
            ) : (
              <Hash className="h-4 w-4 text-white/40" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-white font-semibold truncate">
              {activeChannel ? (
                <>
                  <span className="text-white/45 font-normal">#</span>
                  {activeChannel.name}
                </>
              ) : (
                "Select a channel"
              )}
            </div>
            {activeChannel ? (
              String(activeChannel.channel_type || "text").toLowerCase() === "announcement" ? (
                <div className="text-xs text-amber-200/55 truncate">Announcements · moderators only</div>
              ) : activeChannel?.topic ? (
                <div className="text-xs text-white/50 truncate">{activeChannel.topic}</div>
              ) : (
                <div className="text-xs text-white/40 truncate">
                  Club chat · separate from the feed
                </div>
              )
            ) : null}
          </div>

          <button
            type="button"
            onClick={() => setMembersPanelOpen((v) => !v)}
            className="xl:hidden h-9 px-3 rounded-xl border border-white/12 text-xs text-white/75 inline-flex items-center gap-1.5"
          >
            <Users className="h-3.5 w-3.5" />
            Members
          </button>
        </header>

        {msgErr ? (
          <div className="mx-4 mt-3 rounded-xl border border-red-400/20 bg-red-500/10 px-3 py-2 text-sm text-red-200">
            {msgErr}
          </div>
        ) : null}

        <div
          ref={listRef}
          className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-2.5"
        >
          {hasOlder ? (
            <button
              type="button"
              onClick={onLoadOlder}
              disabled={loadingOlder || msgLoading}
              className="w-full py-2 text-xs text-white/50 hover:text-white/80 flex items-center justify-center gap-1 border border-dashed border-white/10 rounded-xl hover:bg-white/[0.03] transition"
            >
              {loadingOlder ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )}
              Load older messages
            </button>
          ) : null}

          {msgLoading && messages.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-white/45 gap-2 text-sm">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading messages…
            </div>
          ) : null}

          {!msgLoading && messages.length === 0 && activeChannel ? (
            <div className="text-center py-16 text-white/45 text-sm">
              No messages yet in this channel.
            </div>
          ) : null}

          {messages.map((m) => {
            const av = resolveAvatar(m.profile_picture);
            return (
              <div
                key={m.id}
                className="flex gap-3 group rounded-xl px-2 py-1 hover:bg-white/[0.03]"
              >
                <div className="h-9 w-9 rounded-full border border-white/10 bg-white/[0.05] overflow-hidden shrink-0 flex items-center justify-center text-xs font-semibold text-white/70">
                  {av ? (
                    <img src={av} alt="" className="h-full w-full object-cover" />
                  ) : (
                    String(m.username || "?").slice(0, 1).toUpperCase()
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-white/90">
                      {m.username || "Member"}
                    </span>
                    <span className="text-[10px] text-white/35">{formatTs(m.created_at)}</span>
                  </div>
                  <div className="mt-0.5 text-sm text-white/75 whitespace-pre-wrap break-words">
                    {m.content}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {typingIndicatorText ? (
          <div className="px-4 py-2 text-[12px] text-white/45">{typingIndicatorText}</div>
        ) : null}

        <form
          onSubmit={onSend}
          className="shrink-0 relative z-10 p-3 border-t border-white/10 bg-[#080c14]"
        >
          <div className="flex gap-2 items-end">
            <textarea
              value={draft}
              onChange={(e) => {
                const next = e.target.value;
                setDraft(next);

                if (!canPostInActiveChannel || !activeId) return;
                if (!socketRef.current) return;

                // Emit typing:start on each input change.
                // Backend dedupes active typers and only refreshes its timeout without rebroadcasting.
                typingActiveRef.current = true;
                try {
                  socketRef.current.emit("club-channel:typing:start", { channelId: activeId });
                } catch {
                  // ignore
                }

                if (typingStopTimeoutRef.current) {
                  clearTimeout(typingStopTimeoutRef.current);
                }

                typingStopTimeoutRef.current = setTimeout(() => {
                  typingActiveRef.current = false;
                  try {
                    socketRef.current?.emit("club-channel:typing:stop", { channelId: activeId });
                  } catch {
                    // ignore
                  }
                }, typingStopDelayMs);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onSend(e);
                }
              }}
              rows={2}
              maxLength={4000}
              readOnly={false}
              autoComplete="off"
              placeholder={
                !activeChannel
                  ? "Select a channel"
                  : !canPostInActiveChannel
                    ? "Only moderators can post in announcements"
                    : `Message #${activeChannel.name}`
              }
              disabled={!canPostInActiveChannel}
              className={cn(
                "flex-1 min-w-0 rounded-xl bg-[#121826] border px-3 py-2 text-sm text-white placeholder:text-white/35 outline-none resize-none min-h-[44px] max-h-32 disabled:opacity-50 disabled:cursor-not-allowed",
                accentRing
              )}
            />
            <button
              type="submit"
              disabled={sending || !draft.trim() || !canPostInActiveChannel}
              className={cn(
                "h-11 w-11 shrink-0 rounded-xl border inline-flex items-center justify-center transition disabled:opacity-40",
                accentBtn
              )}
            >
              {sending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </button>
          </div>
          <div className="mt-1.5 text-[10px] text-white/35">
            Shift+Enter for newline. Live delivery is enabled via websockets.
          </div>
        </form>
      </div>

      {showMembersDesktopColumn ? (
        <aside className="hidden xl:block w-[300px] shrink-0">
          <ClubMembersTab club={club} />
        </aside>
      ) : null}

      {membersPanelOpen ? (
        <div className="xl:hidden fixed inset-0 z-[9998] flex">
          <button
            type="button"
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            aria-label="Close"
            onClick={() => setMembersPanelOpen(false)}
          />
          <div className="relative ml-auto h-full w-[min(92vw,360px)] bg-[#060812] border-l border-white/10 p-3 overflow-y-auto shadow-2xl">
            <div className="flex justify-end mb-2">
              <button
                type="button"
                onClick={() => setMembersPanelOpen(false)}
                className="h-9 w-9 rounded-xl border border-white/12 inline-flex items-center justify-center text-white/70"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <ClubMembersTab club={club} />
          </div>
        </div>
      ) : null}

      {createOpen ? (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center px-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/75 backdrop-blur-sm"
            aria-label="Close"
            onClick={() => !creating && setCreateOpen(false)}
          />
          <div className="relative w-full max-w-md rounded-2xl border border-white/12 bg-[#0a1020] p-5 shadow-2xl">
            <div className="text-white font-semibold text-lg">Create channel</div>
            <p className="mt-1 text-sm text-white/55">
              Text channels are separate from the club feed. Announcement channels only allow
              moderators to post.
            </p>
            <form onSubmit={onCreateChannel} className="mt-5 space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-white/45">
                  Name
                </label>
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  maxLength={100}
                  className="mt-1 w-full h-11 rounded-xl border border-white/12 bg-black/30 px-3 text-sm text-white outline-none focus:border-white/20"
                  placeholder="e.g. spoilers"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-white/45">
                  Type
                </label>
                <select
                  value={newType}
                  onChange={(e) => setNewType(e.target.value)}
                  className="mt-1 w-full h-11 rounded-xl border border-white/12 bg-black/30 px-3 text-sm text-white outline-none"
                >
                  <option value="text">Text</option>
                  <option value="announcement">Announcement (mods only)</option>
                  <option value="event">Event (reserved)</option>
                </select>
              </div>
              {createErr ? (
                <div className="text-sm text-red-300">{createErr}</div>
              ) : null}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  disabled={creating}
                  onClick={() => setCreateOpen(false)}
                  className="h-10 px-4 rounded-xl border border-white/12 text-sm text-white/80"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || !newName.trim()}
                  className={cn(
                    "h-10 px-5 rounded-xl border text-sm font-semibold transition disabled:opacity-40",
                    accentBtn
                  )}
                >
                  {creating ? "Creating…" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
